import React, { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Button, Modal } from "react-bootstrap";
import ApiService from "../../../../services/ApiService";

const AI_ASSISTANT_ENABLED = (() => {
  if (process.env.NODE_ENV === "production") return false;

  const rawFlag = `${process.env.REACT_APP_DYNAMIC_EVENTS_AI_ASSISTANT_ENABLED || ""}`
    .trim()
    .toLowerCase();

  if (rawFlag === "false") return false;

  return true;
})();

function hasPatchContent(patch = {}) {
  if (!patch || typeof patch !== "object") return false;
  const keys = Object.keys(patch);
  if (!keys.length) return false;
  if (keys.some((key) => key !== "pageContent" && key !== "calendarOptions")) {
    return true;
  }
  if (patch.pageContent && Object.keys(patch.pageContent).length) return true;
  if (patch.calendarOptions && Object.keys(patch.calendarOptions).length) return true;
  return false;
}

const DynamicEventAssistant = ({
  employee,
  mode = "create",
  getCurrentDraft,
  onApplyPatch,
  canSave,
}) => {
  const [showAssistant, setShowAssistant] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [pendingPatch, setPendingPatch] = useState({});
  const [missingFields, setMissingFields] = useState([]);
  const [followUpQuestions, setFollowUpQuestions] = useState([]);
  const [readyToSave, setReadyToSave] = useState(false);
  const [confidence, setConfidence] = useState("");
  const [lastExamplesUsed, setLastExamplesUsed] = useState([]);

  const modeLabel = mode === "edit" ? "Edit Event" : "Create Event";
  const patchAvailable = useMemo(() => hasPatchContent(pendingPatch), [pendingPatch]);

  const pushAssistantMessage = (content) => {
    const text = `${content || ""}`.trim();
    if (!text.length) return;
    setMessages((prev) => [...prev, { role: "assistant", content: text }].slice(-40));
  };

  const runAssistant = async () => {
    const userPrompt = `${prompt || ""}`.trim();
    if (!userPrompt.length) {
      return toast.error("Please describe what you want to generate.");
    }
    if (!employee) {
      return toast.error("Please sign in before using the AI assistant.");
    }

    const userMessage = { role: "user", content: userPrompt };
    const outboundConversation = [...messages.slice(-9), userMessage];
    setMessages((prev) => [...prev, userMessage].slice(-40));
    setPrompt("");
    setLoading(true);

    try {
      const api = new ApiService({ employee });
      const response = await api.post("landmark/dynamicevents/ai/draftEventFields", {
        prompt: userPrompt,
        mode,
        currentEventDraft:
          typeof getCurrentDraft === "function" ? getCurrentDraft() : {},
        conversation: outboundConversation,
      });

      if (response?.error) {
        pushAssistantMessage(response.error);
        return toast.error(response.error);
      }

      const assistantMessage =
        response?.assistantMessage ||
        "Draft generated. Review the suggestions before applying.";
      pushAssistantMessage(assistantMessage);
      setPendingPatch(response?.fieldPatch || {});
      setMissingFields(Array.isArray(response?.missingFields) ? response.missingFields : []);
      setFollowUpQuestions(
        Array.isArray(response?.followUpQuestions) ? response.followUpQuestions : []
      );
      setReadyToSave(!!response?.readyToSave);
      setConfidence(`${response?.confidence || ""}`.trim());
      setLastExamplesUsed(
        Array.isArray(response?.exampleEventsUsed) ? response.exampleEventsUsed : []
      );
    } catch (error) {
      console.error("DynamicEventAssistant runAssistant error", error);
      const errorMsg = "Unable to generate suggestions right now.";
      pushAssistantMessage(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyPatch = (saveAfterApply = false) => {
    if (!patchAvailable || typeof onApplyPatch !== "function") {
      return;
    }
    if (saveAfterApply) {
      const confirmed = window.confirm(
        "Apply the AI draft to the form and save this event now?"
      );
      if (!confirmed) {
        return;
      }
    }

    const result = onApplyPatch(pendingPatch, { saveAfterApply });
    if (result === false) {
      return;
    }

    toast.success(
      saveAfterApply
        ? "AI draft applied. Save has been queued."
        : "AI draft applied to the form."
    );
  };

  if (!AI_ASSISTANT_ENABLED) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        className="btn btn-primary dynamic-event-ai-button"
        onClick={() => setShowAssistant(true)}
      >
        <i className="fa-solid fa-wand-magic-sparkles mr-2"></i>
        AI Assistant
      </button>

      <Modal
        show={showAssistant}
        onHide={() => setShowAssistant(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Dynamic Event AI Assistant</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="dynamic-event-ai-subtext">
            {modeLabel} with natural language. The assistant drafts fields and asks
            follow-up questions before save.
          </div>

          <div className="dynamic-event-ai-messages">
            {messages.length === 0 ? (
              <div className="dynamic-event-ai-empty">
                Try: Build a VIP dinner page for a fintech dinner in Chicago on June 27.
              </div>
            ) : (
              messages.map((message, index) => (
                <div
                  key={`${message.role}-${index}`}
                  className={`dynamic-event-ai-message ${message.role}`}
                >
                  <div className="role">{message.role === "assistant" ? "AI" : "You"}</div>
                  <div className="content">{message.content}</div>
                </div>
              ))
            )}
          </div>

          {followUpQuestions.length > 0 && (
            <div className="dynamic-event-ai-followups">
              <div className="title">Follow-up questions</div>
              {followUpQuestions.map((entry, index) => (
                <div key={`${entry.field || "field"}-${index}`} className="question">
                  <span className="field">{entry.field}</span>
                  <span>{entry.question}</span>
                </div>
              ))}
            </div>
          )}

          {missingFields.length > 0 && (
            <div className="dynamic-event-ai-missing">
              <div className="title">Missing fields before save</div>
              <div className="chips">
                {missingFields.map((field, index) => (
                  <span key={`${field}-${index}`} className="chip">
                    {field}
                  </span>
                ))}
              </div>
            </div>
          )}

          {confidence ? (
            <div className="dynamic-event-ai-metadata">
              Confidence: <b>{confidence}</b>
              {readyToSave ? " - Ready to save" : ""}
            </div>
          ) : null}

          {lastExamplesUsed.length > 0 ? (
            <div className="dynamic-event-ai-metadata">
              Example events used: {lastExamplesUsed.join(", ")}
            </div>
          ) : null}

          <textarea
            className="form-control dynamic-event-ai-input"
            rows={4}
            placeholder="Describe the event you want to build..."
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            disabled={loading}
          />
        </Modal.Body>
        <Modal.Footer className="dynamic-event-ai-footer">
          <div className="dynamic-event-ai-footer-left">
            {patchAvailable && (
              <>
                <Button
                  variant="secondary"
                  onClick={() => handleApplyPatch(false)}
                  disabled={loading}
                >
                  Apply to Form
                </Button>
                <Button
                  variant="primary"
                  onClick={() => handleApplyPatch(true)}
                  disabled={loading || !canSave}
                >
                  Apply &amp; Save
                </Button>
              </>
            )}
          </div>
          <Button variant="primary" onClick={runAssistant} disabled={loading}>
            {loading ? "Generating..." : "Generate Draft"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default DynamicEventAssistant;
